import { Theme } from '@eversports/react-components/design-tokens/theme'
import useMediaQuery from '@eversports/react-components/design-tokens/useMediaQuery'
import useTheme from '@eversports/react-components/design-tokens/useTheme'
import AdvantageWrapper from '@eversports/react-components/patterns/AdvantageWrapper'
import Flex from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import MonetaryValue from '@eversports/react-components/primitives/MonetaryValue'
import Text from '@eversports/react-components/primitives/Text'
import { makeStyles } from '@mui/styles'
import React from 'react'

import FormattedContent from '@eversports/react-components/primitives/FormattedContent'
import { Localized } from '../../localization/react'
import { Membership } from '../../typings/Product'

import ValidActivities from './ValidActivities'
import HTMLFormattedContentWrapper, { onATag, phoenixAllowList } from './HTMLFormattedContentWrapper'
import MembershipPromotionInfo from './MembershipPromotionInfo'

const useStyles = makeStyles((theme: Theme) => ({
  membershipLeftContainer: {
    flex: 0.7,
  },
  membershipRightContainer: {
    flex: 0.3,
    [theme.breakpoints.down('md')]: {
      marginRight: theme.layoutSpacing.hero,
    },
  },
  strikethrough: {
    color: theme.palette.text.disabled,
    textDecoration: 'line-through',
  },
}))

interface Props {
  membership: Membership
}

const MembershipModalContent: React.FC<React.PropsWithChildren<Props>> = ({ membership }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isInMobileView = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <Flex flexDirection={['column', 'column', 'row']} verticalSpacing="wide" flexWrap="wrap">
      <Flex
        flexDirection="column"
        verticalSpacing="wide"
        marginRight="hero"
        className={classes.membershipLeftContainer}
      >
        <Flex alignItems="flex-start" justifyContent="space-between" flexWrap="wrap">
          <Flex alignItems="baseline">
            <Heading is="h5" color="primary.main" variant={isInMobileView ? 'large' : 'x-large'}>
              <MonetaryValue
                value={membership.paymentPlan.promotion?.price.value || membership.paymentPlan.recurring.value}
                currencyCode={membership.paymentPlan.recurring.currency}
              />
            </Heading>
            <Text color="primary.main" size="small">
              <Localized
                id="membership-modal-interval"
                params={{
                  interval: (
                    <Localized
                      id="interval"
                      params={{
                        intervalValue: membership.paymentPlan.interval.value,
                        intervalType: membership.paymentPlan.interval.type as any,
                      }}
                    />
                  ),
                }}
              />
            </Text>
          </Flex>
          <Flex flexDirection="column" marginLeft={['none', 'none', 'wide']}>
            <AdvantageWrapper>
              <Localized
                id={membership.runtime.renewal ? 'membership-min-runtime' : 'membership-runtime'}
                params={{
                  interval: (
                    <Localized
                      id="interval-including-single-number"
                      params={{
                        intervalValue: membership.runtime.initial.value,
                        intervalType: membership.runtime.initial.type as any,
                      }}
                    />
                  ),
                }}
              />
            </AdvantageWrapper>
            <AdvantageWrapper>
              {membership.usages.length ? (
                membership.usages[0].repeating && (
                  <Localized
                    id="membership-usages"
                    params={{
                      usageLimit: membership.usages[0].usageLimit,
                      usageLimitUnit: membership.usages[0].unit,
                      interval: (
                        <Localized
                          id="interval"
                          params={{
                            intervalValue: membership.usages[0].repeating.interval.value,
                            intervalType: membership.usages[0].repeating.interval.type as any,
                          }}
                        />
                      ),
                    }}
                  />
                )
              ) : (
                <Localized id="unlimited-membership-usage" />
              )}
            </AdvantageWrapper>
          </Flex>
        </Flex>
        {membership.paymentPlan.promotion && (
          <Flex flexDirection="column" verticalSpacing="dense">
            <Flex className={classes.strikethrough} flexDirection="row">
              <MonetaryValue
                value={membership.paymentPlan.recurring.value}
                currencyCode={membership.paymentPlan.recurring.currency}
              />
              <Localized
                id="membership-modal-interval"
                params={{
                  interval: (
                    <Localized
                      id="interval"
                      params={{
                        intervalValue: membership.paymentPlan.interval.value,
                        intervalType: membership.paymentPlan.interval.type as any,
                      }}
                    />
                  ),
                }}
              />
            </Flex>
            <MembershipPromotionInfo
              promotion={membership.paymentPlan.promotion}
              recurringPrice={membership.paymentPlan.recurring}
              paymentInterval={membership.paymentPlan.interval}
            />
          </Flex>
        )}
        {membership.conditions && (
          <div>
            <Text size="small" fontWeight="bold" marginBottom="dense">
              <Localized id="membership-conditions" />
            </Text>
            <FormattedContent
              htmlContent={membership.conditions}
              WrapperComponent={HTMLFormattedContentWrapper}
              allowList={phoenixAllowList}
              onTag={onATag}
            />
          </div>
        )}
        <div>
          <Text size="small" fontWeight="bold" marginBottom="dense">
            <Localized id="membership-payment-terms-title" />
          </Text>
          {membership.partialPriceInformation && (
            <Text>
              <Localized
                id="first-partial-payment-info"
                params={{
                  start: new Date(membership.partialPriceInformation.timeRange.start),
                  end: new Date(membership.partialPriceInformation.timeRange.end),
                  partialPrice: (
                    <MonetaryValue
                      value={membership.partialPriceInformation.price.value}
                      currencyCode={membership.partialPriceInformation.price.currency}
                    />
                  ),
                  fullPrice: (
                    <MonetaryValue
                      value={membership.paymentPlan.recurring.value}
                      currencyCode={membership.paymentPlan.recurring.currency}
                    />
                  ),
                  dueDate: (
                    <Localized
                      id="membership-due-date"
                      params={{
                        intervalType: membership.paymentPlan.interval.type as any,
                        offset: membership.paymentPlan.offset,
                      }}
                    />
                  ),
                }}
              />
            </Text>
          )}
          <Text>{membership.paymentAndCancellationTerms || <Localized id="membership-payment-terms-info" />}</Text>
        </div>
      </Flex>
      <Flex flexDirection="column" verticalSpacing="wide" className={classes.membershipRightContainer}>
        {membership.partialPriceInformation && (
          <>
            <div>
              <Text size="small">
                <Localized id="partial-payment" />
              </Text>
              <Flex alignItems="center" horizontalSpacing="dense" marginBottom="dense">
                <Text color="primary.main" fontWeight="bold">
                  <MonetaryValue
                    value={membership.partialPriceInformation.price.value}
                    currencyCode={membership.partialPriceInformation.price.currency}
                  />
                </Text>
                <Text size="x-small" color="primary.main">
                  <Localized
                    id="partial-payment-interval"
                    params={{
                      start: new Date(membership.partialPriceInformation.timeRange.start),
                      end: new Date(membership.partialPriceInformation.timeRange.end),
                    }}
                  />
                </Text>
              </Flex>
              <Text size="small">
                <Localized
                  id="partial-payment-disclaimer"
                  params={{
                    dueDate: (
                      <Localized
                        id="membership-due-date"
                        params={{
                          intervalType: membership.paymentPlan.interval.type as any,
                          offset: membership.paymentPlan.offset,
                        }}
                      />
                    ),
                  }}
                />
              </Text>
            </div>
            <div>
              <Text size="small">
                <Localized id="starting-date" />
              </Text>
              <Text>
                <Localized
                  id="membership-starting-date"
                  params={{ startingDate: new Date(membership.partialPriceInformation.timeRange.start) }}
                />
              </Text>
            </div>
          </>
        )}
        {membership.runtime.renewal && (
          <>
            <div>
              <Text size="small">
                <Localized id="automatic-extension" />
              </Text>
              <Text>
                <Localized
                  id="interval-including-single-number"
                  params={{
                    intervalValue: membership.runtime.renewal.interval.value,
                    intervalType: membership.runtime.renewal.interval.type as any,
                  }}
                />
              </Text>
            </div>
            {membership.runtime.renewal.cancellation.value !== 0 && (
              <div>
                <Text size="small">
                  <Localized id="cancellation-period" />
                </Text>
                <Text>
                  <Localized
                    id="cancellation-period-interval"
                    params={{
                      interval: (
                        <Localized
                          id="interval-including-single-number"
                          params={{
                            intervalValue: membership.runtime.renewal.cancellation.value,
                            intervalType: membership.runtime.renewal.cancellation.type as any,
                          }}
                        />
                      ),
                    }}
                  />
                </Text>
              </div>
            )}
          </>
        )}
        {membership.activities.length > 0 && <ValidActivities activities={membership.activities} />}
      </Flex>
    </Flex>
  )
}

export default MembershipModalContent
